/**
 * React Query hook for fetching story tasks
 * Story 199: View story details with PRD and design
 */

import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';

/** Evidence data from task completion */
export interface TaskEvidence {
  files_changed?: Array<{ path: string; change: string; lines?: string }>;
  rationale?: string;
}

/** Test results from task completion */
export interface TaskTestResults {
  passed?: number;
  failed?: number;
  coverage?: number;
}

/** Task item from API (Story 289: includes evidence fields) */
export interface TaskItem {
  seq: number;
  title: string;
  body: string | null;
  status: 'proposed' | 'in-progress' | 'done';
  assignee: string | null;
  implements: string[] | null;
  task_type: string | null;
  has_evidence: boolean;
  evidence: TaskEvidence | null;
  test_results: TaskTestResults | null;
}

/** Tasks list response from API */
export interface TaskListResponse {
  tasks: TaskItem[];
}

/** Query key factory for story tasks */
export const storyTaskKeys = {
  all: ['story-tasks'] as const,
  list: (projectSlug: string, storyId: string) =>
    [...storyTaskKeys.all, projectSlug, storyId] as const,
};

/** Fetch tasks for a story */
async function fetchStoryTasks(
  projectSlug: string,
  storyId: string
): Promise<TaskListResponse> {
  const endpoint = `/api/v1/projects/${encodeURIComponent(projectSlug)}/stories/${storyId}/tasks`;
  return api.fetch(endpoint);
}

/**
 * Hook to fetch a story's tasks
 *
 * @param projectSlug - Project identifier (e.g., "boxabirds/claude-backlog")
 * @param storyId - Story external ID
 */
export function useStoryTasks(projectSlug: string, storyId: string | null) {
  return useQuery({
    queryKey: storyTaskKeys.list(projectSlug, storyId ?? ''),
    queryFn: () => fetchStoryTasks(projectSlug, storyId!),
    enabled: !!projectSlug && !!storyId,
  });
}
